import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "./AddBreakdown.css";

const EditBreakdown = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    journey_plane_no: "",
    scheduled_vehicle: "",
    driver_name: "",
    driver_phone: "",
    carrier: "",
    issue: "",
    remarks: "",
    truck_status: "",
    breakdown_time: "",
    breakdow_status: "",
    breakdown_id: "",
    jp_status: "",
    ivms_point: "",
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [trucks, setTrucks] = useState([]);
  const navigate = useNavigate();

  const getCurrentLocalDateTime = (dateString) => {
    const localDate = new Date(dateString);
    if (isNaN(localDate.getTime())) return ""; 
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const convertToUTC = (localDateTime) => {
    const date = new Date(localDateTime);
    if (isNaN(date.getTime())) {
      console.error("Invalid date value:", localDateTime);
      return null;
    }
    return date.toISOString();
  };

  // Add fetchBreakdownRecord to useCallback
  const fetchBreakdownRecord = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/breakdownRecords/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // support: response.data can be an array, an object or { data: object }
      let rec = response.data;
      if (Array.isArray(rec)) rec = rec[0] || {};
      if (rec && rec.data) rec = rec.data;
      const norm = {};
      Object.keys(rec || {}).forEach((k) => (norm[k.toLowerCase()] = rec[k]));

      const updatedFormData = {
        journey_plane_no: norm.journey_plane_no || "",
        scheduled_vehicle: norm.scheduled_vehicle || "",
        driver_name: norm.driver_name || "",
        driver_phone: norm.driver_phone || "",
        carrier: norm.carrier || "",
        issue: norm.issue || "",
        remarks: norm.remarks || "",
        truck_status: norm.truck_status || "Loaded",
        // store local datetime string for the input (convert on submit)
        breakdown_time: norm.breakdown_time ? getCurrentLocalDateTime(norm.breakdown_time) : "",
        breakdow_status: norm.breakdow_status || "",
        breakdown_id: norm.breakdown_id || "",
        jp_status: norm.jp_status || "IN_TRANSIT",
        ivms_point: norm.ivms_point || "",
      };
      setFormData(updatedFormData);
      setInitialFormData(updatedFormData);
    } catch (error) {
      console.error("Error fetching breakdown record:", error);
    }
  }, [id]); // Add id as dependency

  useEffect(() => {
    fetchBreakdownRecord();
    fetchInTransitTrucks();
  }, [fetchBreakdownRecord]); // Update dependency array

  const fetchInTransitTrucks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/inTransitTrucks`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // normalize truck keys to lowercase for robust lookup
      const raw = response.data || [];
      const normalized = (Array.isArray(raw) ? raw : []).map((t) => {
        const o = {};
        Object.keys(t || {}).forEach((k) => (o[k.toLowerCase()] = t[k]));
        return o;
      });
      setTrucks(normalized);
    } catch (error) {
      console.error("Error fetching in-transit trucks:", error);
    }
  };

  const handleTruckChange = (e) => {
    const { name, value } = e.target;

    if (!value) {
      setFormData({
        ...formData,
        journey_plane_no: "",
        scheduled_vehicle: "",
        driver_name: "",
        driver_phone: "",
        carrier: "",
        ivms_point: "",
      });
      return;
    }

    const selectedTruck = trucks.find(
      (truck) =>
        (name === "journey_plane_no" && truck.journey_plane_no === value) ||
        (name === "scheduled_vehicle" && truck.scheduled_vehicle === value)
    );

    if (selectedTruck) {
      setFormData({
        ...formData,
        journey_plane_no: selectedTruck.journey_plane_no,
        scheduled_vehicle: selectedTruck.scheduled_vehicle,
        driver_name: selectedTruck.driver_name,
        driver_phone: selectedTruck.driver_phone,
        carrier: selectedTruck.carrier,
        ivms_point: selectedTruck.ivms_point,
      });
    } else {
      // If no truck found, just update the changed field
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // store local value for inputs; convert to UTC only on submit
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.truck_status) {
      console.error("Truck Status is required."); // Log error if not selected
      return;
    }
    try {
      const token = localStorage.getItem("token");

      // Prepare data to submit by filtering out unchanged fields
      const dataToSubmit = { breakdown_id: formData.breakdown_id }; // Always include breakdown_id
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== initialFormData[key] && key !== "breakdown_id") {
          dataToSubmit[key] = formData[key];
        }
      });

      // Convert breakdown_time (local) to UTC if present in modified fields
      if (dataToSubmit.breakdown_time) {
        dataToSubmit.breakdown_time = convertToUTC(dataToSubmit.breakdown_time);
      }

      if (Object.keys(dataToSubmit).length === 1) {
        // Only breakdown_id is present
        console.log("No changes detected, skipping API call.");
        return;
      }

      await axios.patch(
        `${process.env.REACT_APP_BASE_API_URL}/breakdownRecords/${id}`,
        dataToSubmit,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      navigate("/breakdown-tracker");
    } catch (error) {
      console.error("Error updating breakdown record:", error);
    }
  };

  return (
    <div className="container-add-breakdown">
      <button
        className="close-button"
        onClick={() => navigate("/breakdown-tracker")}
      >
        ×
      </button>
      <form onSubmit={handleSubmit} className="form-grid-breakdown">
        <input type="hidden" name="breakdown_id" value={formData.breakdown_id} />

        <div className="block-display-breakdown">
          <label className="form-label-breakdown">
            Truck Number <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown"
            name="scheduled_vehicle"
            value={formData.scheduled_vehicle}
            onChange={handleTruckChange}
            list="vehicle-list"
            required
          />
          <datalist id="vehicle-list">
            {trucks.map((truck) => (
              <option
                key={truck.scheduled_vehicle}
                value={truck.scheduled_vehicle}
              />
            ))}
          </datalist>
        </div>
        <div>
          <label className="form-label-breakdown">
            Breakdown Date<span className="required">*</span>
          </label>
          <input
            type="datetime-local"
            className="form-input-breakdown"
            name="breakdown_time"
            value={formData.breakdown_time}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Journey Number <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown"
            name="journey_plane_no"
            value={formData.journey_plane_no}
            onChange={handleTruckChange}
            list="journey-list"
            required
          />
          <datalist id="journey-list">
            {trucks.map((truck) => (
              <option
                key={truck.journey_plane_no}
                value={truck.journey_plane_no}
              />
            ))}
          </datalist>
        </div>
        <div>
          <label className="form-label-breakdown">
            Driver Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown"
            name="driver_name"
            value={formData.driver_name}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Driver Phone Number <span className="required">*</span>
          </label>
          <input
            type="tel"
            className="form-input-breakdown"
            name="driver_phone"
            value={formData.driver_phone}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Last Check Point <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown"
            name="ivms_point"
            value={formData.ivms_point || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Carrier <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown"
            name="carrier"
            value={formData.carrier}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Issue <span className="required">*</span>
          </label>
          <select
            className="form-input-breakdown form-select-breakdown"
            name="issue"
            value={formData.issue}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Issue --</option>
            <option value="Electrical">Electrical</option>
            <option value="Mechanical">Mechanical</option>
            <option value="Tires">Tires</option>
            <option value="Incident">Incident</option>
          </select>
        </div>
        <div>
          <label className="form-label-breakdown">
            Remarks <span className="required">*</span>
          </label>
          <textarea
            className="form-input-breakdown"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            rows="3"
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Truck Status <span className="required">*</span>
          </label>
          <select
            className="form-input-breakdown form-select-breakdown"
            name="truck_status"
            value={formData.truck_status}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Status --</option>{" "}
            {/* Blank default option */}
            <option value="Loaded">Loaded</option>
            <option value="Empty">Empty</option>
          </select>
        </div>
        <div>
          <label className="form-label-breakdown">
            JP Status <span className="required">*</span>
          </label>
          <select
            className="form-input-breakdown form-select-breakdown"
            name="jp_status"
            value={formData.jp_status}
            onChange={handleChange}
            required
          >
            <option value="IN_TRANSIT">IN TRANSIT</option>
            <option value="CLOSED">CLOSED</option>
          </select>
        </div>

        <button type="submit" className="submit-button-breakdown1">
          Save
        </button>
      </form>
    </div>
  );
};

export default EditBreakdown;
